import { useCallback } from 'react';

import type { ApolloClient } from '@apollo/client';

import {
  useScreenerFlow,
  type ResponderInsightStore,
  type ScreenerAnswerFailureOutcome,
} from '@/shared-responder-v2';
import type { ScreenerFlowState } from '@/shared-responder-v2-core';
import type { ResponderPanel } from '@/shared-survey';

import {
  answerScreenerQuestion,
  startScreenerSession,
} from './shared-responder-v2-gateway-screener-gateway';
import {
  resolveScreenerEventRedirectUrl,
  toScreenerAnswerEvent,
  toScreenerAnswerFailureEvent,
} from './shared-responder-v2-gateway-screener.helpers';

function replaceWindowLocation(url: string): void {
  window.location.replace(url);
}

interface UseResponderScreenerInput {
  readonly client: ApolloClient;
  readonly projectId: string;
  readonly channel: string | null;
  readonly panel?: ResponderPanel | null;
  readonly hasScreener: boolean;
  readonly insightStore?: ResponderInsightStore;
  readonly navigate?: (url: string) => void;
  readonly isDeferred?: boolean;
}

/**
 * Drives the pre-survey screener loop. `onKeyRejected` is for the insight
 * seam: when `initiateUnifiedSession` rejects the key the screener issued,
 * the only recovery is a fresh screener session, which is the same restart a
 * session past its TTL takes.
 */
export function useResponderScreener({
  client,
  projectId,
  channel,
  panel = null,
  hasScreener,
  insightStore,
  navigate = replaceWindowLocation,
  isDeferred = false,
}: UseResponderScreenerInput): {
  readonly state: ScreenerFlowState;
  readonly onToggleOption: (value: string) => void;
  readonly onContinue: () => void;
  readonly onSelectAndContinue: (value: string) => void;
  readonly onKeyRejected: () => void;
} {
  const startSession = useCallback(
    () => startScreenerSession(client, { projectId, channel }),
    [channel, client, projectId]
  );

  return useScreenerFlow({
    projectId,
    hasScreener,
    insightStore,
    isDeferred,
    startSession,
    answerQuestion: async ({ sessionId, answer }) => {
      const result = await answerScreenerQuestion(client, {
        sessionId,
        answer,
      });
      return toScreenerAnswerEvent(result);
    },
    onAnswerEvent: (event) => {
      const redirectUrl = resolveScreenerEventRedirectUrl(panel, event);
      if (redirectUrl !== null) {
        navigate(redirectUrl);
      }
    },
    onAnswerFailure: (error): ScreenerAnswerFailureOutcome => {
      const event = toScreenerAnswerFailureEvent(error);
      const redirectUrl = resolveScreenerEventRedirectUrl(panel, event);
      if (redirectUrl !== null) {
        navigate(redirectUrl);
      }

      if (event.type === 'restarted') {
        return { kind: 'restart' };
      }
      if (event.type === 'failed') {
        console.error('Failed to submit the screener answer', error);
      }
      return { kind: 'dispatch', event };
    },
  });
}
